import React from 'react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

/**
 * The one confirmation every delete in the app asks for.
 *
 * Deleting is soft — a deleted record goes to the recycle bin and can be
 * restored for 90 days (see .claude/skills/soft-delete) — so the question is
 * short and the same everywhere, worded after SharePoint's own. Use this rather
 * than `window.confirm`, which looks like the browser, not the app.
 *
 *   <DeleteConfirmDialog
 *     open={confirmOpen}
 *     onOpenChange={setConfirmOpen}
 *     count={selected.length}
 *     onConfirm={handleDelete}
 *   />
 *
 * `onConfirm` may return a promise; the dialog stays open, with Delete
 * disabled, until it settles, then closes.
 */
export function deleteConfirmMessage(count = 1) {
  return count === 1
    ? 'Are you sure you want to send this item to the recycle bin?'
    : `Are you sure you want to send these ${count} items to the recycle bin?`;
}

export default function DeleteConfirmDialog({ open, onOpenChange, count = 1, onConfirm }) {
  const [busy, setBusy] = React.useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
      onOpenChange?.(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange?.(next); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete?</DialogTitle>
          <DialogDescription>{deleteConfirmMessage(count)}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
